const MPESA_PERSONAL_ROLE = 'PERSONAL';
const MPESA_BUSINESS_ROLE = 'BUSINESS';

export const MPESA_WALLET_COPY = {
    personalPochiToggle: 'Enable this when the PERSONAL wallet can have a linked BUSINESS child row under the same phone number.',
    personalReady: 'Save this PERSONAL wallet first, then create or edit the BUSINESS child row for the same phone number.',
    businessParent: 'Select the PERSONAL wallet that owns this BUSINESS row. The parent wallet must already have Pochi enabled.',
    lockedWallet: 'This PERSONAL wallet already has BUSINESS children. Edit the linked BUSINESS row instead of changing the phone, role, parent, or Pochi settings here.',
    businessOpeningBalance: 'This seeds the opening balance for the linked BUSINESS wallet under the same phone number.',
};

export const isMpesaAccount = (account) => (account?.account_type || '').toUpperCase() === 'MPESA';

export const getMpesaRole = (account) => (isMpesaAccount(account) ? (account?.mpesa_role || MPESA_PERSONAL_ROLE).toUpperCase() : '');

export const getMpesaRoleColor = (role) => {
    if (role === MPESA_BUSINESS_ROLE) return 'blue';
    return 'purple';
};

export const getMpesaParentAccount = (account, accountLookup = {}) => {
    if (!isMpesaAccount(account) || getMpesaRole(account) !== MPESA_BUSINESS_ROLE) return null;

    return accountLookup[String(account.mpesa_parent_account_id || '')] || null;
};

export const getMpesaLinkedBusinessChild = (account, accounts = []) => {
    if (!isMpesaAccount(account) || getMpesaRole(account) !== MPESA_PERSONAL_ROLE) return null;

    const accountId = String(account?.id || '');
    return accounts.find((childAccount) => (
        isMpesaAccount(childAccount)
        && getMpesaRole(childAccount) === MPESA_BUSINESS_ROLE
        && String(childAccount.mpesa_parent_account_id || '') === accountId
    )) || null;
};

export const hasLinkedBusinessChildren = (account, accounts = []) => {
    if (!isMpesaAccount(account) || getMpesaRole(account) !== MPESA_PERSONAL_ROLE) return false;

    const accountId = String(account?.id || '');
    return accounts.some((childAccount) => (
        isMpesaAccount(childAccount)
        && getMpesaRole(childAccount) === MPESA_BUSINESS_ROLE
        && String(childAccount.mpesa_parent_account_id || '') === accountId
    ));
};

export const isValidBusinessParent = (candidate, childPhone) => {
    if (!isMpesaAccount(candidate) || getMpesaRole(candidate) !== MPESA_PERSONAL_ROLE) return false;
    if (!Boolean(candidate.has_pochi)) return false;
    if (childPhone && candidate.mpesa_phone && candidate.mpesa_phone.trim() !== childPhone) return false;

    return true;
};

export const isLockedMpesaParent = (account, accounts = []) => hasLinkedBusinessChildren(account, accounts);

export const getMpesaIdentityFieldValue = (formData, field) => {
    if (field === 'has_pochi') return Boolean(formData?.[field]);
    if (field === 'mpesa_parent_account_id') return String(formData?.[field] || '');
    return String(formData?.[field] || '');
};

export const getMpesaWalletDescriptor = (account, accountLookup = {}, accounts = []) => {
    if (!isMpesaAccount(account)) return '';

    const role = getMpesaRole(account) || MPESA_PERSONAL_ROLE;

    if (role === MPESA_BUSINESS_ROLE) {
        const parentAccount = getMpesaParentAccount(account, accountLookup);
        const parentName = parentAccount?.account_name || 'Linked personal wallet';
        const parentPhone = parentAccount?.mpesa_phone ? ` · ${parentAccount.mpesa_phone}` : '';
        return `${role} wallet · Parent: ${parentName}${parentPhone}`;
    }

    const linkedBusinessChild = getMpesaLinkedBusinessChild(account, accounts);
    const linkedBusinessName = linkedBusinessChild?.account_name ? ` · Linked BUSINESS: ${linkedBusinessChild.account_name}` : '';
    const linkedBusinessPhone = linkedBusinessChild?.mpesa_phone ? ` · ${linkedBusinessChild.mpesa_phone}` : '';

    return `${role} wallet${Boolean(account.has_pochi) ? ' · Pochi enabled' : ''}${linkedBusinessName}${linkedBusinessPhone}`;
};

export const getMpesaWalletOptionLabel = (account, accountLookup = {}, accounts = []) => {
    if (!account) return '';

    const descriptor = getMpesaWalletDescriptor(account, accountLookup, accounts);
    return `${account.account_name || 'Unnamed account'}${descriptor ? ` · ${descriptor}` : ''}`;
};

export const getMpesaWalletDisplayInfo = (account, accountLookup = {}, accounts = []) => {
    if (!isMpesaAccount(account)) return null;

    const role = getMpesaRole(account) || MPESA_PERSONAL_ROLE;
    const descriptor = getMpesaWalletDescriptor(account, accountLookup, accounts);
    const parentAccount = getMpesaParentAccount(account, accountLookup);
    const linkedBusinessChild = getMpesaLinkedBusinessChild(account, accounts);

    const chips = role === MPESA_BUSINESS_ROLE
        ? [
            { label: role, color: getMpesaRoleColor(role) },
            parentAccount ? { label: `Parent: ${parentAccount.account_name || 'Linked personal wallet'}`, color: 'blue-gray' } : null,
        ]
        : [
            { label: role, color: getMpesaRoleColor(role) },
            account.has_pochi ? { label: 'POCHI ENABLED', color: 'amber' } : null,
            linkedBusinessChild ? { label: 'LINKED BUSINESS', color: 'blue-gray' } : null,
        ];

    return {
        role,
        roleColor: getMpesaRoleColor(role),
        descriptor,
        optionLabel: getMpesaWalletOptionLabel(account, accountLookup, accounts),
        parentAccount,
        linkedBusinessChild,
        hasPochi: Boolean(account.has_pochi),
        chips: chips.filter(Boolean),
    };
};

export const getMpesaWalletCopyBundle = (displayInfo) => {
    if (!displayInfo) {
        return {
            selectorSubtitle: '',
            summaryText: '',
        };
    }

    const copyText = displayInfo.descriptor || 'M-Pesa wallet';

    return {
        selectorSubtitle: copyText,
        summaryText: copyText,
    };
};

export const getMpesaWalletPresentation = (account, accountLookup = {}, accounts = []) => {
    const displayInfo = getMpesaWalletDisplayInfo(account, accountLookup, accounts);
    const copy = getMpesaWalletCopyBundle(displayInfo);

    if (!displayInfo) {
        return {
            isMpesa: false,
            label: account?.account_name || '',
            subtitle: account?.account_type || '',
            copy,
            descriptor: '',
            chips: [],
            displayInfo: null,
        };
    }

    return {
        isMpesa: true,
        label: account?.account_name || 'Unnamed account',
        subtitle: copy.selectorSubtitle,
        copy,
        descriptor: displayInfo.descriptor,
        optionLabel: displayInfo.optionLabel,
        chips: displayInfo.chips,
        displayInfo,
    };
};

export const applyMpesaFormStateChange = (prev = {}, updates = {}, accounts = []) => {
    const nextState = {
        ...prev,
        ...updates,
    };

    const nextAccountType = String(nextState.account_type || '').toUpperCase();

    if (nextAccountType !== 'MPESA') {
        return {
            ...nextState,
            account_type: nextState.account_type || 'INCOME',
            bank_account_number: '',
            bank_name: '',
            mpesa_phone: '',
            mpesa_role: 'PERSONAL',
            mpesa_parent_account_id: '',
            has_pochi: false,
            business_opening_balance: 0,
        };
    }

    const nextRole = String(nextState.mpesa_role || MPESA_PERSONAL_ROLE).toUpperCase();
    const nextPhone = String(nextState.mpesa_phone || '').trim();
    const normalizedState = {
        ...nextState,
        account_type: 'MPESA',
        mpesa_role: nextRole,
    };

    if (nextRole === MPESA_BUSINESS_ROLE) {
        const parentAccount = accounts.find((candidate) => String(candidate.id) === String(normalizedState.mpesa_parent_account_id || ''));

        if (!parentAccount || !isValidBusinessParent(parentAccount, nextPhone)) {
            normalizedState.mpesa_parent_account_id = '';
        }
        normalizedState.has_pochi = false;
        normalizedState.business_opening_balance = 0;
    } else {
        normalizedState.mpesa_parent_account_id = '';
        normalizedState.has_pochi = Boolean(nextState.has_pochi);
    }

    if (Object.prototype.hasOwnProperty.call(updates, 'mpesa_phone') && normalizedState.mpesa_parent_account_id) {
        const parentAccount = accounts.find((candidate) => String(candidate.id) === String(normalizedState.mpesa_parent_account_id));

        if (!parentAccount || !isValidBusinessParent(parentAccount, nextPhone)) {
            normalizedState.mpesa_parent_account_id = '';
        }
    }

    if (nextRole === MPESA_PERSONAL_ROLE && !Boolean(normalizedState.has_pochi)) {
        normalizedState.business_opening_balance = 0;
    }

    return normalizedState;
};

export const normalizeMpesaFormState = applyMpesaFormStateChange;
